import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Lock, Phone, Building2, ArrowRight, LogIn } from "lucide-react";
import { toast } from "sonner";
import { api, setToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthHeader from "@/components/AuthHeader";

export default function Registar() {
    const { user, refreshMe } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", company: "" });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) navigate(user.role === "admin" ? "/admin" : "/conta", { replace: true });
    }, [user, navigate]);

    function onChange(e) {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        if (form.password.length < 8) {
            toast.error("A password tem de ter no mínimo 8 caracteres.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/register", {
                name: form.name.trim(),
                email: form.email.trim(),
                password: form.password,
                phone: form.phone.trim() || null,
                company: form.company.trim() || null,
            });
            setToken(data.access_token);
            await refreshMe();
            toast.success("Conta criada com sucesso!");
            navigate("/conta", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Não foi possível criar a conta.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <section className="relative min-h-[100vh] bg-muted flex items-center justify-center px-6 pt-28 pb-20" data-testid="registar-page">
            <AuthHeader />
            <div className="w-full max-w-lg">
                <div className="rounded-2xl border border-border bg-white p-6 sm:p-8 shadow-lg">
                    <span className="eyebrow">Criar conta</span>
                    <h1 className="mt-3 font-heading text-3xl font-extrabold text-primary leading-tight">Juntar-se à NOVIFOOD</h1>
                    <p className="mt-2 text-sm text-muted-foreground">Submeta pedidos de orçamento, gira os seus equipamentos comprados e abra pedidos de garantia.</p>

                    <form onSubmit={onSubmit} className="mt-7 grid gap-4 sm:grid-cols-2" data-testid="registar-form">
                        <InputField icon={User} label="Nome completo" name="name" value={form.name} onChange={onChange} required testid="registar-name" />
                        <InputField icon={Mail} label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="registar-email" />
                        <InputField icon={Lock} label="Password (mín. 8)" name="password" type="password" value={form.password} onChange={onChange} required testid="registar-password" />
                        <InputField icon={Phone} label="Telemóvel" name="phone" value={form.phone} onChange={onChange} testid="registar-phone" />
                        <div className="sm:col-span-2">
                            <InputField icon={Building2} label="Empresa (opcional)" name="company" value={form.company} onChange={onChange} testid="registar-company" />
                        </div>
                        <button
                            type="submit" disabled={loading}
                            data-testid="registar-submit"
                            className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground shadow-sm transition hover:-translate-y-0.5 disabled:opacity-60"
                        >
                            {loading ? "A criar conta..." : "Criar conta"} <ArrowRight className="h-4 w-4" />
                        </button>
                    </form>
                </div>
                <div className="mt-6 text-center">
                    <p className="text-sm text-muted-foreground">Já tem conta?</p>
                    <Link to="/entrar" data-testid="registar-goto-entrar"
                        className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/70">
                        <LogIn className="h-4 w-4" /> Iniciar sessão
                    </Link>
                </div>
            </div>
        </section>
    );
}

function InputField({ icon: Icon, label, name, value, onChange, type = "text", required, testid }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}</label>
            <div className="mt-2 relative">
                <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                    type={type} name={name} value={value} onChange={onChange} required={required}
                    data-testid={testid}
                    className="w-full rounded-md border border-border bg-muted pl-10 pr-3 py-3 text-sm text-primary outline-none focus:border-primary focus:bg-white"
                />
            </div>
        </div>
    );
}
